import * as KnownProperties from "../known-properties";
import { PropertyValue, PropertyValueSet } from "@genesys/property";
import { getIfIntegerChanged } from "../helper-functions";
import { OperatingCaseGroupEnum } from "../../../graphql-types";
import { Amount } from "uom";
import { Units } from "uom-units";

export const manualInputProperty = KnownProperties.roomControlManualInput;

export const overridableProperties = [
  KnownProperties.roomMinTemperature,
  KnownProperties.roomMaxTemperature,
  KnownProperties.roomTemperature,
  KnownProperties.roomHumidityAbsolute,
  KnownProperties.roomHumidityRelative,
  KnownProperties.roomHumidityDewPoint,
  KnownProperties.roomMinHumidityAbsolute,
  KnownProperties.roomMinHumidityRelative,
  KnownProperties.roomMinHumidityDewPoint,
  KnownProperties.roomMaxHumidityAbsolute,
  KnownProperties.roomMaxHumidityRelative,
  KnownProperties.roomMaxHumidityDewPoint
];

export const properties = [
  KnownProperties.roomControlManualInput,
  KnownProperties.rooomTemperatureControl,
  KnownProperties.roomMinTemperature,
  KnownProperties.roomMaxTemperature,
  KnownProperties.roomTemperature,
  KnownProperties.roomHumidityControl,
  KnownProperties.roomHumidityControlType,
  KnownProperties.roomHumidityAbsolute,
  KnownProperties.roomHumidityRelative,
  KnownProperties.roomHumidityDewPoint,
  KnownProperties.roomMinHumidityAbsolute,
  KnownProperties.roomMinHumidityRelative,
  KnownProperties.roomMinHumidityDewPoint,
  KnownProperties.roomMaxHumidityAbsolute,
  KnownProperties.roomMaxHumidityRelative,
  KnownProperties.roomMaxHumidityRelativeAdjusted,
  KnownProperties.roomMaxHumidityDewPoint
];

export const caseOverridesDefault: {
  readonly [operatingCaseGroup in OperatingCaseGroupEnum]: PropertyValueSet.PropertyValueSet;
} = {
  JANUARY: PropertyValueSet.Empty,
  FEBRUARY: PropertyValueSet.Empty,
  MARCH: PropertyValueSet.Empty,
  APRIL: PropertyValueSet.Empty,
  MAY: PropertyValueSet.Empty,
  JUNE: PropertyValueSet.Empty,
  JULY: PropertyValueSet.Empty,
  AUGUST: PropertyValueSet.Empty,
  SEPTEMBER: PropertyValueSet.Empty,
  OCTOBER: PropertyValueSet.Empty,
  NOVEMBER: PropertyValueSet.Empty,
  DECEMBER: PropertyValueSet.Empty
};

export function getPropertyUpdates(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  let defaults = getPropertyDefaults(previousSelections, currentSelections);

  return defaults;
}

export function getApplicationTypeUpdates(
  _applicationType: number
): ReadonlyArray<{
  readonly propertyName: string;
  readonly propertyValue: number;
}> {
  return [];
}

function getPropertyDefaults(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  let defaults = PropertyValueSet.Empty;

  defaults = PropertyValueSet.merge(
    temperatureControlDefaults(
      previousSelections,
      PropertyValueSet.merge(defaults, currentSelections)
    ),
    defaults
  );

  defaults = PropertyValueSet.merge(
    humidityControlDefaults(
      previousSelections,
      PropertyValueSet.merge(defaults, currentSelections)
    ),
    defaults
  );

  return defaults;
}

function temperatureControlDefaults(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  let defaults = PropertyValueSet.Empty;

  const temperatureControl = getIfIntegerChanged(
    previousSelections,
    currentSelections,
    KnownProperties.rooomTemperatureControl
  );
  if (temperatureControl !== undefined) {
    defaults = PropertyValueSet.merge(
      setTemperaratureControlBasedRoomMaxHumidityRelativeAdjustedDefaults(
        temperatureControl,
        currentSelections
      ),
      defaults
    );
  }

  if (temperatureControl !== undefined) {
    defaults = PropertyValueSet.merge(
      setTemperatureDefaults(temperatureControl),
      defaults
    );
  }

  return defaults;
}

function humidityControlDefaults(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  let defaults = PropertyValueSet.Empty;

  const humidityControl = getIfIntegerChanged(
    previousSelections,
    currentSelections,
    KnownProperties.roomHumidityControl
  );

  if (humidityControl !== undefined) {
    defaults = PropertyValueSet.merge(
      humidityControlDefaultValues(humidityControl),
      defaults
    );
  }

  const humidityControlType = getIfIntegerChanged(
    previousSelections,
    PropertyValueSet.merge(defaults, currentSelections),
    KnownProperties.roomHumidityControlType
  );

  if (humidityControlType !== undefined) {
    defaults = PropertyValueSet.merge(
      useRoomHumidityControlTypeDefaults(
        humidityControlType,
        PropertyValueSet.merge(defaults, currentSelections)
      ),
      defaults
    );
  }

  if (humidityControl !== undefined) {
    defaults = PropertyValueSet.merge(
      setAbsHumidityDefaults(humidityControl),
      defaults
    );

    defaults = PropertyValueSet.merge(
      setDewPointHumidityDefaults(humidityControl),
      defaults
    );

    defaults = PropertyValueSet.merge(
      setRelativeHumidityDefaults(humidityControl),
      defaults
    );
  }

  return defaults;
}

function humidityControlDefaultValues(
  propertyValue: number | undefined
): PropertyValueSet.PropertyValueSet {
  switch (propertyValue) {
    case 0:
      return {
        [KnownProperties.roomHumidityControlType]: PropertyValue.fromInteger(0)
      };
    case 1:
    case 2:
    case 3:
    case 4:
      return {
        [KnownProperties.roomHumidityControlType]: PropertyValue.fromInteger(1)
      };
    case undefined:
      return PropertyValueSet.Empty;
    default:
      throw new Error("Invalid value.");
  }
}
function setTemperatureDefaults(
  propertyValue: number
): PropertyValueSet.PropertyValueSet {
  switch (propertyValue) {
    case 0:
    case 1:
    case 2:
    case 3:
      return {
        [KnownProperties.roomMinTemperature]: PropertyValue.fromAmount(
          Amount.create(15, Units.Celsius)
        ),
        [KnownProperties.roomMaxTemperature]: PropertyValue.fromAmount(
          Amount.create(15, Units.Celsius)
        ),
        [KnownProperties.roomTemperature]: PropertyValue.fromAmount(
          Amount.create(15, Units.Celsius)
        )
      };
    case 4:
      return {
        [KnownProperties.roomMinTemperature]: PropertyValue.fromAmount(
          Amount.create(15, Units.Celsius)
        ),
        [KnownProperties.roomMaxTemperature]: PropertyValue.fromAmount(
          Amount.create(25, Units.Celsius)
        ),
        [KnownProperties.roomTemperature]: PropertyValue.fromAmount(
          Amount.create(15, Units.Celsius)
        )
      };
    default:
      throw new Error("Invalid value.");
  }
}

function setAbsHumidityDefaults(
  propertyValue: number
): PropertyValueSet.PropertyValueSet {
  switch (propertyValue) {
    case 0:
    case 1:
    case 2:
    case 3:
      return {
        [KnownProperties.roomMinHumidityAbsolute]: PropertyValue.fromAmount(
          Amount.create(5, Units.GramPerKilogram)
        ),
        [KnownProperties.roomMaxHumidityAbsolute]: PropertyValue.fromAmount(
          Amount.create(5, Units.GramPerKilogram)
        ),
        [KnownProperties.roomHumidityAbsolute]: PropertyValue.fromAmount(
          Amount.create(5, Units.GramPerKilogram)
        )
      };
    case 4:
      return {
        [KnownProperties.roomMinHumidityAbsolute]: PropertyValue.fromAmount(
          Amount.create(2, Units.GramPerKilogram)
        ),
        [KnownProperties.roomMaxHumidityAbsolute]: PropertyValue.fromAmount(
          Amount.create(5, Units.GramPerKilogram)
        ),
        [KnownProperties.roomHumidityAbsolute]: PropertyValue.fromAmount(
          Amount.create(5, Units.GramPerKilogram)
        )
      };
    default:
      throw new Error("Invalid value.");
  }
}

function setDewPointHumidityDefaults(
  propertyValue: number
): PropertyValueSet.PropertyValueSet {
  switch (propertyValue) {
    case 0:
    case 1:
    case 2:
    case 3:
      return {
        [KnownProperties.roomMinHumidityDewPoint]: PropertyValue.fromAmount(
          Amount.create(5, Units.CelsiusDewPoint)
        ),
        [KnownProperties.roomMaxHumidityDewPoint]: PropertyValue.fromAmount(
          Amount.create(5, Units.CelsiusDewPoint)
        ),
        [KnownProperties.roomHumidityDewPoint]: PropertyValue.fromAmount(
          Amount.create(5, Units.CelsiusDewPoint)
        )
      };
    case 4:
      return {
        [KnownProperties.roomMinHumidityDewPoint]: PropertyValue.fromAmount(
          Amount.create(2, Units.CelsiusDewPoint)
        ),
        [KnownProperties.roomMaxHumidityDewPoint]: PropertyValue.fromAmount(
          Amount.create(5, Units.CelsiusDewPoint)
        ),
        [KnownProperties.roomHumidityDewPoint]: PropertyValue.fromAmount(
          Amount.create(5, Units.CelsiusDewPoint)
        )
      };
    default:
      throw new Error("Invalid value.");
  }
}
function setRelativeHumidityDefaults(
  propertyValue: number
): PropertyValueSet.PropertyValueSet {
  switch (propertyValue) {
    case 0:
    case 1:
    case 2:
    case 3:
      return {
        [KnownProperties.roomMinHumidityRelative]: PropertyValue.fromAmount(
          Amount.create(50, Units.PercentHumidity)
        ),
        [KnownProperties.roomMaxHumidityRelative]: PropertyValue.fromAmount(
          Amount.create(50, Units.PercentHumidity)
        ),
        [KnownProperties.roomHumidityRelative]: PropertyValue.fromAmount(
          Amount.create(50, Units.PercentHumidity)
        )
      };
    case 4:
      return {
        [KnownProperties.roomMinHumidityRelative]: PropertyValue.fromAmount(
          Amount.create(25, Units.PercentHumidity)
        ),
        [KnownProperties.roomMaxHumidityRelative]: PropertyValue.fromAmount(
          Amount.create(50, Units.PercentHumidity)
        ),
        [KnownProperties.roomHumidityRelative]: PropertyValue.fromAmount(
          Amount.create(50, Units.PercentHumidity)
        )
      };
    default:
      throw new Error("Invalid value.");
  }
}

function setTemperaratureControlBasedRoomMaxHumidityRelativeAdjustedDefaults(
  propertyValue: number,
  current: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  switch (propertyValue) {
    case 1:
    case 2:
    case 3:
    case 4:
      return {
        [KnownProperties.roomMaxHumidityRelativeAdjusted]:
          PropertyValue.fromInteger(0)
      };
    case 0:
      return {
        [KnownProperties.roomMaxHumidityRelativeAdjusted]:
          PropertyValueSet.getInteger(
            KnownProperties.roomHumidityControl,
            current
          ) === 2 &&
          PropertyValueSet.getInteger(
            KnownProperties.roomHumidityControlType,
            current
          ) === 2
            ? PropertyValue.fromInteger(1)
            : PropertyValue.fromInteger(0)
      };
    default:
      throw new Error("Invalid value.");
  }
}

function useRoomHumidityControlTypeDefaults(
  humidityControlType: number | undefined,
  current: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  switch (humidityControlType) {
    case 0:
    case 1:
    case 3:
      return {
        [KnownProperties.roomMaxHumidityRelativeAdjusted]:
          PropertyValue.fromInteger(0)
      };

    case 2:
      return {
        [KnownProperties.roomMaxHumidityRelativeAdjusted]:
          PropertyValueSet.getInteger(
            KnownProperties.roomHumidityControl,
            current
          ) === 2
            ? PropertyValue.fromInteger(1)
            : PropertyValue.fromInteger(0)
      };

    case undefined:
      return PropertyValueSet.Empty;
    default:
      throw new Error("Invalid value.");
  }
}
